'use client';

import { InvitePageContent } from './invite-page-content';
import { useCommonAuthProvider } from '@modules/auth/providers/CommonAuthProvider/CommonAuthProvider';
import {
  buildOrganizationReturnToAfterInvite,
  waitForOrganizationMembership,
} from '@modules/auth/utils/invite-utils';
import { useUser } from 'app/_providers/User';
import useInvites from 'app/_utils/data/invites';

import * as Sentry from '@sentry/nextjs';
import { useSearchParams } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import * as React from 'react';

import type { InvitePageError } from './invite-page-error-view';

export const InvitePage: React.FC = () => {
  const auth = useCommonAuthProvider();
  const { user, loginWithRedirect, isLoading, logout } = auth;
  const { memberships } = useUser();
  const searchParams = useSearchParams();
  const code = searchParams?.get('code');
  const tenantId = searchParams?.get('tenantId');
  const loginHint = searchParams?.get('login_hint');

  const [message, setMessage] = useState('Loading invitation');
  const [error, setError] = useState<InvitePageError | undefined>();
  const [acceptedOrgId, setAcceptedOrgId] = useState<string | undefined>();
  const [membershipWaitFailed, setMembershipWaitFailed] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const acceptInviteCalled = useRef(false);
  const { acceptInvite } = useInvites();
  const hasError = Boolean(error);

  const redirectToAcceptedOrganization = (orgId: string): void => {
    loginWithRedirect({
      organizationId: orgId,
      returnTo: buildOrganizationReturnToAfterInvite(orgId),
    });
  };

  const waitForAcceptedOrganizationMembership = async (orgId: string): Promise<boolean> => {
    setMessage('Confirming account access');
    setMembershipWaitFailed(false);

    const membershipReady = await waitForOrganizationMembership(memberships.mutate, orgId);

    if (!membershipReady) {
      setMembershipWaitFailed(true);
    }

    return membershipReady;
  };

  const handleAcceptInvite = async () => {
    const userId = user?.sub;
    const sentryCaptureContext = userId ? { user: { id: userId } } : undefined;

    try {
      setMessage('Accepting invitation');

      if (userId) {
        Sentry.captureMessage('User on invitation page', sentryCaptureContext);
      }

      if (!code || code === 'null' || !tenantId) {
        throw Error('Missing required params');
      }

      await acceptInvite(code, tenantId);
      setAcceptedOrgId(tenantId);

      const membershipReady = await waitForAcceptedOrganizationMembership(tenantId);

      if (membershipReady) {
        redirectToAcceptedOrganization(tenantId);
      }
    } catch (e: unknown) {
      const caughtError = e as { info?: { message?: string; details?: { action?: InvitePageError['action'] } } };
      const returnedError = caughtError.info?.message as string;

      if (returnedError) {
        Sentry.captureException(returnedError, sentryCaptureContext);
        setError({
          action: caughtError.info?.details?.action ?? { type: 'unresolvable', message: returnedError },
        });
      } else {
        setError({ action: { type: 'unknown' } });
      }
    } finally {
      setInitialLoad(false);
    }
  };

  const handleRetryMembershipWait = async (): Promise<void> => {
    if (!acceptedOrgId) {
      return;
    }

    const membershipReady = await waitForAcceptedOrganizationMembership(acceptedOrgId);

    if (membershipReady) {
      redirectToAcceptedOrganization(acceptedOrgId);
    }
  };

  useEffect(() => {
    if (isLoading || hasError) {
      return;
    }

    // Compare emails in lowercase to account for case differences
    const isWrongUserEmail =
      !!loginHint && (user?.email?.toLowerCase() !== loginHint.toLowerCase());

    if (isWrongUserEmail) {
      // Set the error state with the detailed message that instructs the user which email to use.
      setError({ action: { type: 'change-user', loginHint } });
      setInitialLoad(false);

      return;
    }

    const canAcceptInvite = acceptedOrgId === undefined && !acceptInviteCalled.current;

    if (!canAcceptInvite) {
      return;
    }

    acceptInviteCalled.current = true;
    handleAcceptInvite();
  }, [acceptedOrgId, hasError, isLoading, loginHint, logout, user]);

  return (
    <InvitePageContent
      isAuthLoading={isLoading}
      message={message}
      initialLoad={initialLoad}
      acceptedOrgId={acceptedOrgId}
      membershipWaitFailed={membershipWaitFailed}
      error={error}
      errorAuth={auth}
      onRetryMembershipWait={() => void handleRetryMembershipWait()}
    />
  );
};
